import { useFormatter, useTranslations } from 'use-intl';
import { CreateLiquidityPositionDetails, Step } from '../../types';
import Collapsible from '@/presentation/components/modules/Collapsible';
import ActionButton from '@/presentation/components/elements/ActionButton';
import Icon, { Icons } from '@/presentation/components/elements/Icon';
import { parseEther } from 'viem';
import FormattedEther from '@/presentation/components/elements/FormattedEther/FormattedEther';
import { Dispatch, ReactNode, SetStateAction } from 'react';

export interface ReviewProps {
  state: CreateLiquidityPositionDetails;
  symbol: string;
  collectionName: string;
  setStep: Dispatch<SetStateAction<Step>>;
  using: 'nft' | 'vtoken';
}

export const Review = ({
  state,
  symbol,
  setStep,
  collectionName,
  using,
}: ReviewProps) => {
  const t = useTranslations('CreateLiquidityPosition.Review');
  const { number } = useFormatter();
  const totalNFTCount = state.items.reduce(
    (total, item) => total + Number(item.quantity ?? 1),
    0
  );
  const liquidityNFTCount = Number(state.tokenAmount);
  const inventoryNFTCount =
    using === 'nft' ? totalNFTCount - liquidityNFTCount : 0;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-medium text-base">{t('title')}</h1>
      </div>
      <Collapsible
        header={
          <div className="flex justify-between items-center flex-1">
            <h1 className="uppercase text-xs font-bold">
              {t('position.title')}
            </h1>
            <span className="bg-gradient-pink-orange bg-clip-text text-transparent font-bold">
              {collectionName}
              {' / '}
              {'ETH'}
            </span>
          </div>
        }
        open={true}
      >
        <dl className="space-y-2 p-4">
          <div className="flex justify-between items-baseline">
            <dt>{t('position.pool')}</dt>
            <span className="flex-1 border-b border-dotted border-gray-300 h-0 mx-4"></span>
            <dd>{t('position.poolName', { symbol })}</dd>
          </div>
          <div className="flex justify-between items-baseline">
            <dt>{t('position.feeTier')}</dt>
            <span className="flex-1 border-b border-dotted border-gray-300 h-0 mx-4"></span>
            <dd>
              {number(Number(state.feeTier) / 1_000_000, {
                style: 'percent',
                minimumSignificantDigits: 1,
                maximumSignificantDigits: 2,
              })}
            </dd>
          </div>
          <div className="flex justify-between items-baseline">
            <dt>{t('position.ethDeposit')}</dt>
            <span className="flex-1 border-b border-dotted border-gray-300 h-0 mx-4"></span>
            <dd>
              {!!Number(state.ethAmount) ? (
                <FormattedEther value={parseEther(state.ethAmount)} />
              ) : (
                '--'
              )}
            </dd>
          </div>
          <div className="flex justify-between items-baseline">
            <dt>
              {t(
                state.using === 'nft'
                  ? 'position.nftDeposit'
                  : 'position.tokenDeposit',
                { symbol }
              )}
            </dt>
            <span className="flex-1 border-b border-dotted border-gray-300 h-0 mx-4"></span>
            <dd>{liquidityNFTCount > 0 ? number(liquidityNFTCount) : '--'}</dd>
          </div>
          <div className="flex justify-between items-baseline">
            <dt>{t('position.range')}</dt>
            <span className="flex-1 border-b border-dotted border-gray-300 h-0 mx-4"></span>
            <dd>
              {t('position.rangeDetails', {
                from: state.minPriceInEth,
                to: state.maxPriceInEth,
              })}
            </dd>
          </div>
        </dl>
      </Collapsible>
      {inventoryNFTCount > 0 && (
        <Collapsible
          header={
            <div className="flex items-center flex-1 justify-between">
              <Icon
                name={Icons.EXCLAMATION_TRIANGLE}
                size={20}
                className="mr-2"
              />
              <h1 className="uppercase text-xs font-bold">
                {t('inventory.title')}
              </h1>
              <span className="ml-auto bg-gradient-pink-orange bg-clip-text text-transparent font-bold">
                {collectionName}
              </span>
            </div>
          }
          open={true}
        >
          <div className="p-4">
            <p className="text-xs max-w-prose pb-2 text-center mx-auto text-gray-800 underline">
              {t.rich('inventory.disclaimer', { br: () => <br></br> })}
            </p>
            <dl className="space-y-2">
              <div className="flex justify-between items-baseline">
                <dt>{t('inventory.items')}</dt>
                <span className="flex-1 border-b border-dotted border-gray-300 h-0 mx-4"></span>
                <dd>
                  {t('inventory.amount', {
                    name: symbol,
                    count: inventoryNFTCount,
                  })}
                </dd>
              </div>
              <div className="flex justify-between items-baseline">
                <dt>{t('inventory.lock')}</dt>
                <span className="flex-1 border-b border-dotted border-gray-300 h-0 mx-4"></span>
                <dd>
                  {t('inventory.lockTime', {
                    hours: 48,
                  })}
                </dd>
              </div>
            </dl>
          </div>
        </Collapsible>
      )}
      <ActionButton
        blendColor="after:bg-gray-50 w-full"
        onClick={() => setStep('processing')}
      >
        {t('create')}
      </ActionButton>
    </div>
  );
};

export default Review;
